<template>
  <div :class="['controls-area']" id="tour-step-01">
    <controls-box :title="lang.file" id="tour-step-02">
      <div :class="['flex', 'equal-width', 'px-0']">
        <button :class="['m-1']" @click="showImportDialog" :title="lang.importLocalData">
          <icon name="folder-open" />
        </button>
        <button :class="['m-1']" @click="exportImage" :title="lang.saveImage">
          <icon name="content-save" />
        </button>
        <button :class="['m-1']" @click="exportData" :title="lang.exportData">
          <icon name="export" />
        </button>
      </div>
    </controls-box>
    <controls-box :title="lang.zoom" id="tour-step-03">
      <select v-model="defaultZoom" :title="lang.zoom" :class="['my-4']">
        <option v-for="(zoom, index) in zooms" :value="zoom" v-html="doZoomLabel(zoom)" :key="index" />
      </select>
    </controls-box>
    <controls-box :title="lang.filters" id="tour-step-04">
      <filters-control />
    </controls-box>
    <controls-box :title="lang.layers" id="tour-step-05">
      <ul :class="['images-list', 'm-2']">
        <li
          @click="selectLayer(image.index)"
          v-for="(image, index) in images"
          :key="image.index"
          :class="['my-1' , 'p-1', index === $root.selectedLayer ? 'selected-layer' : null]">
          <table-layout y-align="middle">
            <row>
              <cell x-align="center" :class="['icon-cell']">
                <icon
                  :font-size="1"
                  :disabled="images.length === 1 && !image.hidden"
                  :title="image.hidden ? lang.showLayer : lang.hideLayer"
                  :class="['cursor-pointer', 'layer-toggle-visibility']"
                  :name="image.hidden ? 'eye-off' : 'eye'"
                  @click.native.stop="toggleLayerVisibility(image.index)" />
              </cell>
              <cell :title="lang.layerId">
                <div :class="['ellipsis', 'images-list-label']" v-html="image.index" />
              </cell>
              <cell x-align="center" :class="['icon-cell']">
                <icon
                  :font-size="1"
                  :disabled="images.length === 1 || index !== $root.selectedLayer"
                  :title="lang.deleteLayer"
                  :class="['cursor-pointer', 'layer-delete']"
                  name="delete"
                  @click.native.stop="deleteLayer(image.index)" />
              </cell>
            </row>
          </table-layout>
        </li>
      </ul>
      <div :class="['flex', 'equal-width', 'm-2']" id="tour-step-06">
        <button @click="addLayer" :title="lang.addLayer">
          <icon name="plus" /><span v-html="lang.addLayer" />
        </button>
      </div>
    </controls-box>
  </div>
</template>
<script>
import mixBlendModes from '../helpers/mix-blend-modes'
import events from '../helpers/events'
import config from '../helpers/config'
import lang from '../helpers/lang'
import zooms from '../helpers/zooms'
import methods from '../helpers/methods'
import FiltersControl from '../components/filters-control'
import htmlToImage from 'html-to-image'
import { saveAs } from 'file-saver'

export default {
  name: 'ControlsArea',
  components: {
    FiltersControl
  },
  mixins: [
    methods
  ],
  data () {
    return {
      lang,
      zooms,
      config,
      mixBlendModes,
      defaultZoom: null
    }
  },
  props: {
    images: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    /**
     * Images array watcher
     */
    images () {
      if (this.images.length === 1) {
        this.setDefaultZoom()
      }
    },
    /**
     * Default zoom watcher
     */
    defaultZoom () {
      if (this.images.length) {
        this.setDefaultZoom()
      }
    }
  },
  mounted () {
    this.onMounted()
  },
  methods: {
    /**
     * Format the zoom value to human-readable label
     * @method doZoomLabel
     * @return {string}
     */
    doZoomLabel (zoom) {
      return `${zoom * 100}%`
    },
    /**
     * Handler for the mounted callback of the component
     * @method onMounted
     * @return {void}
     */
    onMounted () {
      this.defaultZoom = this.zooms[2]
    },
    /**
     * Set the default zoom to the composite image
     * @method setDefaultZoom
     * @return {void}
     */
    setDefaultZoom () {
      const compositeImage = this.getImageNode()
      const editingAreaWidth = this.$root.editingAreaWidth
      if (!this.$root.touched) {
        const imageWidth = this.images[0].width
        const zooms = this.zooms
        if (imageWidth <= editingAreaWidth) {
          this.defaultZoom = zooms[4]
        } else if (imageWidth > editingAreaWidth * 3) {
          this.defaultZoom = zooms[2]
        } else if (imageWidth > editingAreaWidth * 4) {
          this.defaultZoom = zooms[1]
        } else {
          this.defaultZoom = zooms[2]
        }
        this.$root.touched = true
      }
      compositeImage.style.transform = `scale(${this.defaultZoom}) translate(-50%, -50%)`
    },
    /**
     * Get the DOM node of the composite image
     * @method getImageNode
     * @return {Element|null}
     */
    getImageNode () {
      return document.querySelector('.composite-image')
    },
    /**
     * Export the image and prompt download
     * @method exportImage
     * @return {void}
     */
    exportImage () {
      const exporting = 'composite-image-exporting'
      const documentElement = document.querySelector('html')
      documentElement.classList.add(exporting)
      htmlToImage.toJpeg(this.getImageNode())
        .then(blob => {
          documentElement.classList.remove(exporting)
          saveAs(blob, this.config.exportFileName())
        })
    },
    /**
     * Show the import image/data dialog
     * @method showImportDialog
     * @return {void}
     */
    showImportDialog () {
      this.$root.importPane = true
    },
    /**
     * Export composite image as JSON
     * @method exportData
     * @return {void}
     */
    exportData () {
      const blob = new Blob([JSON.stringify({cssHdr: this.images}, null, 2)], {type: 'text/plain;charset=utf-8'})
      saveAs(blob, this.config.exportFileName('json'))
    },
    /**
     * Delete layer by index
     * @method deleteLayer
     * @param {number} index
     * @return {void}
     */
    deleteLayer (index) {
      this.$emit(events.layerDelete, index)
    },
    /**
     * Add new layer
     * @method addLayer
     * @return {void}
     */
    addLayer () {
      this.$emit(events.layerAdd)
    },
    /**
     * Toggle layer visibility by index
     * @method toggleLayerVisibility
     * @return {void}
     */
    toggleLayerVisibility (index) {
      this.$emit(events.layerToggleVisibility, index)
    }
  }
}
</script>
<style>
.controls-area
{
  height: 100vh;
}

.controls-area
{
  overflow: auto;
}

.controls-area .selected-layer
{
  background: var(--darker);
}

.controls-area .images-list li
{
  font-size: 1.5em;
}

.controls-area .images-list
{
  max-height: calc(33px * 5);
  overflow-y: auto;
}

.controls-area .images-list-label
{
  font-size: .6em;
}
</style>



// WEBPACK FOOTER //
// src/components/controls-area.vue